import { Pencil, Trash2, Phone, Layers, Monitor, Play, Lock, LockOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { useTools, useCustomTools } from '@/hooks/useTools';
import { convertUTCToLocalTime, getTimezoneAbbreviation } from '@/utils/timezone';
import { getToolLabel } from '@/utils/tools';

import type { ConfigurationListProps } from './types';

export const ConfigurationList = ({
  items,
  onEdit,
  onDelete,
  onTest,
  onTestWebrtc,
  onToggleLock,
}: ConfigurationListProps) => {
  const navigate = useNavigate();
  const timezoneAbbrev = getTimezoneAbbreviation();
  const { data: tools = [] } = useTools();
  const { data: customToolsData } = useCustomTools();
  const customTools = customToolsData?.data || [];

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
      {items.map((config) => {
        const localStartTime = convertUTCToLocalTime(config.call_window_start);
        const localEndTime = convertUTCToLocalTime(config.call_window_end);
        const versionCount = config.versions?.length || 0;
        const activeVersionNumber = config.active_version?.version_number || '1';

        return (
          <Card
            key={config.id}
            className="flex flex-col overflow-hidden transition-all hover:shadow-md"
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-xl">{config.name}</CardTitle>
                  <div className="text-muted-foreground flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="h-5 px-1.5 font-normal">
                      <Layers className="mr-1 h-3 w-3" />v{activeVersionNumber}
                    </Badge>
                    <span>
                      {versionCount} total version{versionCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {config.locked && (
                    <Badge
                      variant="secondary"
                      className="bg-amber-500/10 text-amber-700 dark:text-amber-400"
                      title="This configuration is locked and cannot be edited"
                    >
                      <Lock className="mr-1 h-3 w-3" />
                      Locked
                    </Badge>
                  )}
                  <Badge
                    variant="secondary"
                    className={
                      config.provider === 'gemini'
                        ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                        : 'bg-green-500/10 text-green-700 dark:text-green-400'
                    }
                  >
                    {config.provider === 'gemini' ? 'Gemini' : 'OpenAI'}
                  </Badge>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 pb-3">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                      Schedule
                    </p>
                    <p className="mt-1 font-medium">
                      {localStartTime} - {localEndTime}
                    </p>
                    <p className="text-muted-foreground text-[10px]">{timezoneAbbrev} time</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs font-medium tracking-wider uppercase">
                      Retry Policy
                    </p>
                    <p className="mt-1 font-medium">{config.retry_limit} attempts</p>
                    <p className="text-muted-foreground text-[10px]">
                      {config.retry_delay_minutes}m delay
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-muted-foreground mb-2 text-xs font-medium tracking-wider uppercase">
                    Enabled Tools
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {config.tools?.map((tool) => (
                      <span
                        key={tool}
                        className="bg-primary/5 text-primary/80 border-primary/10 inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-medium"
                      >
                        {getToolLabel(tool, tools)}
                      </span>
                    ))}
                    {config.custom_tool_ids?.map((toolId) => {
                      const customTool = customTools.find((t) => t.id === toolId);
                      return (
                        <span
                          key={toolId}
                          className="inline-flex items-center rounded border border-purple-500/20 bg-purple-500/5 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:text-purple-400"
                        >
                          {customTool?.name || 'Custom Tool'}
                        </span>
                      );
                    })}
                    {!config.tools?.length && !config.custom_tool_ids?.length && (
                      <span className="text-muted-foreground text-xs italic">No tools enabled</span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>

            <CardFooter className="bg-muted/30 flex-wrap gap-2 border-t pt-4">
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => onTest?.(config)}
                    title="Test Call"
                  >
                    <Phone className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => onTestWebrtc?.(config)}
                    title="Test via WebRTC"
                  >
                    <Monitor className="h-3.5 w-3.5" />
                  </Button>
                  {config.widget_enabled && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2"
                      onClick={() => navigate(`/widget-demo/${config.id}`)}
                      title="Widget Demo"
                    >
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    onClick={() => onToggleLock?.(config)}
                    title={config.locked ? 'Unlock configuration' : 'Lock configuration'}
                  >
                    {config.locked ? (
                      <LockOpen className="h-3.5 w-3.5" />
                    ) : (
                      <Lock className="h-3.5 w-3.5" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3"
                    onClick={() => onEdit?.(config)}
                    disabled={config.locked}
                    title={config.locked ? 'Locked configurations cannot be edited' : undefined}
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Manage
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2"
                    onClick={() => onDelete?.(config)}
                    disabled={config.locked}
                    title={config.locked ? 'Locked configurations cannot be deleted' : undefined}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </CardFooter>
          </Card>
        );
      })}
    </div>
  );
};
